import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { MessageCircle, Save } from "lucide-react";

export const Route = createFileRoute("/admin/whatsapp")({
  head: () => ({ meta: [{ title: "WhatsApp Tracking — Admin" }] }),
  component: AdminWhatsApp,
});

type Order = {
  id: string;
  full_name: string;
  phone: string;
  status: string;
  created_at: string;
  whatsapp_tracking_id: string | null;
};

function waLink(id: string) {
  const trimmed = id.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  const digits = trimmed.replace(/\D/g, "");
  return `https://wa.me/${digits}`;
}

function AdminWhatsApp() {
  const [orders, setOrders] = useState<Order[]>([]);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [search, setSearch] = useState("");

  const load = async () => {
    const { data, error } = await supabase
      .from("orders")
      .select("id, full_name, phone, status, created_at, whatsapp_tracking_id")
      .order("created_at", { ascending: false });
    if (error) return toast.error(error.message);
    setOrders((data as Order[]) ?? []);
  };

  useEffect(() => { load(); }, []);

  const save = async (id: string) => {
    const value = (drafts[id] ?? "").trim() || null;
    setSaving(id);
    const { error } = await supabase
      .from("orders")
      .update({ whatsapp_tracking_id: value } as never)
      .eq("id", id);
    setSaving(null);
    if (error) return toast.error(error.message);
    toast.success("WhatsApp tracking saved");
    setOrders((prev) => prev.map((o) => (o.id === id ? { ...o, whatsapp_tracking_id: value } : o)));
    setDrafts((d) => { const n = { ...d }; delete n[id]; return n; });
  };

  const filtered = orders.filter((o) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return o.full_name.toLowerCase().includes(q) || o.id.toLowerCase().includes(q) || (o.phone ?? "").includes(q);
  });

  return (
    <div className="space-y-5">
      <div className="flex items-center gap-3">
        <MessageCircle className="h-5 w-5 text-[#25D366]" />
        <h2 className="display text-xl">WhatsApp Order Tracking</h2>
      </div>
      <p className="text-sm text-muted-foreground">
        Paste a WhatsApp chat link (e.g. <code className="text-xs">https://wa.me/9198…</code>) or a phone number with country code. Customers will see a "Track on WhatsApp" button on this order.
      </p>

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search by name, phone or order ID"
        className="w-full md:w-80 border border-border bg-background px-3 py-2 rounded text-sm"
      />

      <div className="space-y-3">
        {filtered.length === 0 && (
          <p className="p-5 text-sm text-muted-foreground border border-border rounded-lg bg-card">No orders match.</p>
        )}
        {filtered.map((o) => {
          const draft = drafts[o.id] ?? o.whatsapp_tracking_id ?? "";
          const dirty = draft !== (o.whatsapp_tracking_id ?? "");
          const link = waLink(o.whatsapp_tracking_id ?? "");
          return (
            <div key={o.id} className="rounded-lg border border-border bg-card p-4">
              <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
                <div>
                  <p className="text-sm font-medium">{o.full_name}</p>
                  <p className="text-xs text-muted-foreground font-mono">#{o.id.slice(0, 8)} · {o.phone}</p>
                  <p className="text-xs text-muted-foreground">{new Date(o.created_at).toLocaleString()} · {o.status.replace("_", " ")}</p>
                </div>
                {link && (
                  <a href={link} target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 text-xs px-3 py-1.5 bg-[#25D366] text-white rounded hover:opacity-90">
                    <MessageCircle className="h-3.5 w-3.5" /> Open chat
                  </a>
                )}
              </div>
              <div className="flex flex-wrap gap-2">
                <input
                  value={draft}
                  onChange={(e) => setDrafts((d) => ({ ...d, [o.id]: e.target.value }))}
                  placeholder="https://wa.me/91… or 9198…"
                  className="flex-1 min-w-[240px] border border-border bg-background px-3 py-2 rounded text-sm"
                />
                <button
                  onClick={() => save(o.id)}
                  disabled={!dirty || saving === o.id}
                  className="inline-flex items-center gap-2 px-4 py-2 text-sm bg-foreground text-background rounded disabled:opacity-40"
                >
                  <Save className="h-4 w-4" /> {saving === o.id ? "Saving…" : "Save"}
                </button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
